import React from 'react';
import { BarChart2, ChevronDown, ChevronUp, Check, X, DollarSign, ArrowUpDown, Briefcase, Package } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент для отображения сравнительной таблицы норм провоза багажа разных авиакомпаний
 */
const AirlineComparisonTable = ({ 
  results, 
  formData, 
  airlinesData,
  showAllAirlines,
  setShowAllAirlines
}) => {
  if (!results) return null;

  // Получаем параметры для сравнения
  const { destinationType, travelClass, baggageWeight, baggageCount, carryOnWeight, carryOnCount } = formData;
  
  // Получаем данные всех авиакомпаний для выбранного направления и класса
  const airlinesWithAllowance = airlinesData.map(airline => {
    const allowance = airline.baggageAllowance[destinationType][travelClass];
    const overweightFee = airline.extraBaggageFees[destinationType].overweight;
    const extraPieceFee = airline.extraBaggageFees[destinationType].extraPiece;
    
    // Расчет превышения для текущей авиакомпании
    const baggageWeightExcess = Math.max(0, parseFloat(baggageWeight) - allowance.weight);
    const baggageCountExcess = Math.max(0, parseInt(baggageCount) - allowance.count);
    const carryOnWeightExcess = Math.max(0, parseFloat(carryOnWeight) - allowance.carryOnWeight);
    const carryOnCountExcess = Math.max(0, parseInt(carryOnCount) - allowance.carryOnCount);
    
    // Расчет стоимости перевеса
    let totalFee = 0;
    if (baggageWeightExcess > 0) {
      totalFee += overweightFee.base + baggageWeightExcess * overweightFee.perKg;
    }
    if (baggageCountExcess > 0) {
      totalFee += baggageCountExcess * extraPieceFee;
    }
    if (carryOnWeightExcess > 0 || carryOnCountExcess > 0) {
      totalFee += extraPieceFee;
    }
    
    return {
      airline,
      allowance,
      isSelected: airline.id === results.airline.id,
      baggageWeightExcess,
      baggageCountExcess,
      totalFee,
      isBetterOption: allowance.weight > results.allowedBaggageWeight || 
                      allowance.count > results.allowedBaggageCount ||
                      totalFee < results.totalExcessFee
    };
  });
  
  // Отсортируем авиакомпании: сначала выбранная, затем по возрастанию стоимости перевеса
  const sortedAirlines = [...airlinesWithAllowance].sort((a, b) => {
    if (a.isSelected) return -1;
    if (b.isSelected) return 1;
    return a.totalFee - b.totalFee;
  });
  
  // Отображаем только первые 4 авиакомпании, если пользователь не запросил показ всех
  const displayedAirlines = showAllAirlines ? sortedAirlines : sortedAirlines.slice(0, 4);
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-purple-500" />
          Сравнение с другими авиакомпаниями
        </h3>

        {/* Кнопка переключения показа всех авиакомпаний */}
        {sortedAirlines.length > 4 && (
          <Button
            size="sm"
            onClick={() => setShowAllAirlines(prev => !prev)}
            variant="outline"
            icon={showAllAirlines ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          >
            {showAllAirlines ? 'Скрыть' : `Показать все ${sortedAirlines.length} авиакомпании`}
          </Button>
        )}
      </div>

      {/* Таблица сравнения авиакомпаний */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[600px]">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
            <tr>
              <th scope="col" className="px-4 py-2.5 font-semibold">Авиакомпания</th>
              <th scope="col" className="px-4 py-2.5 font-semibold text-center">
                <div className="flex items-center justify-center gap-1">
                  <Briefcase className="w-4 h-4" />
                  <span>Багаж</span>
                </div>
              </th>
              <th scope="col" className="px-4 py-2.5 font-semibold text-center">
                <div className="flex items-center justify-center gap-1">
                  <Package className="w-4 h-4" />
                  <span>Ручная кладь</span>
                </div>
              </th>
              <th scope="col" className="px-4 py-2.5 font-semibold text-center">
                <div className="flex items-center justify-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  <span>Стоимость перевеса</span>
                </div>
              </th>
              <th scope="col" className="px-4 py-2.5 font-semibold text-center">Сравнение</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {displayedAirlines.map((item) => (
              <tr
                key={item.airline.id}
                className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100 ${
                  item.isSelected ? 'bg-purple-50 dark:bg-purple-900/20' : ''
                }`}
              >
                <td className="px-4 py-3 font-medium whitespace-nowrap">
                  <div className="flex items-center">
                    {item.isSelected && (
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mr-2" />
                    )}
                    {item.airline.name}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="inline-flex flex-col">
                    <span className="font-medium">{item.allowance.weight} кг</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {item.allowance.count} {pluralize(item.allowance.count, 'место', 'места', 'мест')}
                    </span>
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="inline-flex flex-col">
                    <span className="font-medium">{item.allowance.carryOnWeight} кг</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {item.allowance.carryOnCount} {pluralize(item.allowance.carryOnCount, 'место', 'места', 'мест')}
                    </span>
                  </span>
                </td>
                <td className="px-4 py-3 text-center font-medium">
                  {item.totalFee > 0 ? (
                    <span className={item.totalFee < results.totalExcessFee ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
                      {formatCurrency(item.totalFee, 0)}
                    </span>
                  ) : (
                    <span className="text-green-600 dark:text-green-400">Нет перевеса</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {item.isSelected ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                      <Check className="w-3 h-3 mr-1" />
                      Выбрано
                    </span>
                  ) : item.isBetterOption ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                      <ArrowUpDown className="w-3 h-3 mr-1" />
                      Выгоднее
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                      Стандарт
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 text-sm text-blue-700 dark:text-blue-300 rounded-lg border border-blue-100 dark:border-blue-800">
        <p>
          <span className="font-semibold">Примечание:</span> Сравнение основано на текущих нормах багажа 
          и указанных вами параметрах. Правила авиакомпаний могут меняться, для точной информации 
          рекомендуется ознакомиться с официальными правилами на сайте авиакомпании.
        </p>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  const lastDigit = number % 10;
  const lastTwoDigits = number % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return many;
  if (lastDigit === 1) return one;
  if (lastDigit >= 2 && lastDigit <= 4) return few;
  return many;
};

export default AirlineComparisonTable;